import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const { user, setUser, logout } = useAuth();
  const [digits, setDigits] = useState(Array(6).fill(""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wait, setWait] = useState(60);
  const refs = useRef([]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);
  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const submit = async (code) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-email", { code });
      setUser(data.user);
      toast.success("Email verified");
    } catch (err) {
      setError(errorMessage(err));
      setDigits(Array(6).fill(""));
      refs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const setAt = (i, v) => {
    const clean = v.replace(/\D/g, "");
    if (clean.length > 1) {
      const next = clean.slice(0, 6).split("");
      const filled = [...next, ...Array(6 - next.length).fill("")];
      setDigits(filled);
      if (next.length === 6) submit(next.join(""));
      else refs.current[next.length]?.focus();
      return;
    }
    if (clean.length > 1 && i > 0) return setAt(0, clean);
    const next = digits.map((d, j) => (j === i ? clean : d));
    setDigits(next);
    if (clean && i < 5) refs.current[i + 1]?.focus();
    if (next.every(Boolean)) submit(next.join(""));
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-code");
      setWait(data.resend_in || 60);
      setError("");
      toast.success("New code sent");
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-6 pt-20 max-w-md mx-auto" data-testid="verify-email-page">
      <div className="font-display text-xl font-bold tracking-tight mb-10">ROLL <span className="text-[#E50914]">SMARTR</span></div>
      <h1 className="font-display text-3xl font-semibold">Check your email</h1>
      <p className="text-sm text-[#A1A1AA] mt-2">We sent a 6-digit code to <span className="text-white" data-testid="verify-email-address">{user.email}</span>. It expires in 10 minutes.</p>
      <div className="flex gap-2 mt-8" data-testid="verify-code-inputs">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => (refs.current[i] = el)}
            value={d}
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            maxLength={6}
            disabled={busy}
            onChange={(e) => setAt(i, e.target.value)}
            onKeyDown={(e) => e.key === "Backspace" && !d && i > 0 && refs.current[i - 1]?.focus()}
            className="w-full h-14 rounded-xl bg-[#111113] border border-[#27272A] text-center text-2xl font-semibold focus:border-[#E50914] outline-none"
            data-testid={`verify-code-input-${i}`}
          />
        ))}
      </div>
      {error && <p className="text-sm text-[#FF6B6B] mt-3" data-testid="verify-error">{error}</p>}
      {busy && <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mt-3"><Loader2 size={15} className="animate-spin" /> Checking…</div>}
      <div className="mt-8 flex items-center justify-between text-sm">
        <button onClick={resend} disabled={wait > 0} className="font-semibold disabled:text-[#52525B]" data-testid="verify-resend-button">
          {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
        </button>
        <button onClick={logout} className="text-[#A1A1AA]" data-testid="verify-logout-button">Use a different email</button>
      </div>
      <p className="text-[11px] text-[#71717A] mt-8">Can't find it? Check your spam or promotions folder.</p>
    </div>
  );
}
